<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { addMinutes, differenceInSeconds } from 'date-fns'
import formatDate from 'date-fns/format'
import VueBarcode from 'vue-barcode'
import QrcodeVue from 'qrcode.vue'
import moment from 'moment'

import InputMixin from '@/mixins/input'
import userHasPermission from '@/utils/user-has-permission'
import order from '@/store/modules/order'
import { ellipsisEnd, decimalCount } from '@/utils/index.js'
import { countryData } from '@/constants/country-data'
import { validEmail } from '@/utils/validate'
import { PAY_NOW } from '@/constants/payment-types'
import { formatCurrency, formatBarcodeValue } from '@/utils/format'
import Amount from '@/components/Amount/Amount.vue'
import BaseButtonV2 from '@/components/lavender/v2/BaseButton.vue'
import BaseSwitch from '@/components/lavender/v2/BaseSwitch.vue'
import CashierNewOrderDrawerBanner from '@/components/CashierNewOrderDrawerBanner/index.vue'
import { navigateToOrderList } from '@/utils/order-routes'

const ORDER_TYPE_QR_CODE = 'qr-code'
const ORDER_TYPE_PAYMENT_LINK = 'payment-link'
const ORDER_TYPE_STORAGE_KEY = 'preferred-order-type'
export default {
  name: 'CreateOrderDrawer',
  components: {
    VueBarcode,
    QrcodeVue,
    Amount,
    BaseButtonV2,
    BaseSwitch,
    CashierNewOrderDrawerBanner,
  },
  mixins: [InputMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    close: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      minutesTillCheckoutExpires: 15,
      isAdvance: false,
      visibleDialog: false,
      ORDER_TYPE_QR_CODE,
      ORDER_TYPE_PAYMENT_LINK,
      timerSeconds: 0,
      timerInterval: null,
      qrCodeLink: '',
      orderType: ORDER_TYPE_QR_CODE,
      allowTypeSelection: false,
      loading: false,
      creatingOrder: false,
      inStoreOrderCreationTime: null,
      createdOrderResponse: {},
      counter: 0,
      intervalId: null,
      storeSelected: '',
      cacheExpiryTimeInMinutes: '', // will be used when resending the link with expiry time
      dialogs: {
        cancelConfirmationDialog: false,
        loadingDialog: false,
        smsSentDialog: false,
        successDialog: false,
        failedDialog: false,
        qrDialog: false,
        errorDialog: false,
      },
      error: {
        title: '',
        message: '',
        messageUnderline: '',
      },
      linkExpired: false,
      ellipsisEnd,
      decimalCount,
      orderCreationForm: {
        phoneNumber: '',
        store: '',
        locale: 'ar_SA',
        email: '',
        orderReferenceId: '',
        orderReferenceIdQR: '',
        amount: '',
        paymentType: '',
        expiryTime: '',
        subventionType: '',
      },
      campaignOptions: [
        {
          label: this.$t('order_create_campaign_not_eligible'),
          value: 'NOT_ELIGIBLE',
        },
        {
          label: 'SUBVENTION_CAMPAIGN_OFFER_0226',
          value: 'SUBVENTION_CAMPAIGN_OFFER_0226',
        },
      ],
      locales: [
        {
          name: 'Arabic / العربية',
          value: 'ar_SA',
          label: 'ar_SA',
        },
        {
          name: 'English / الإنجليزية',
          value: 'en_US',
          label: 'en_US',
        },
      ],
      defaultTime: new Date(new Date().setHours(0, 5, 0, 0)),
    }
  },
  computed: {
    ...mapGetters('translations', ['currentLocale']),
    ...mapState('order', [
      'merchantBranches',
      'paymentPlans',
      'creatingInStoreOrder',
      'fetchingDetail',
    ]),
    ...mapGetters('order', ['getOrderDetail']),
    ...mapState('barcodeSettings', ['settings']),
    ...mapGetters('profile', ['profile', 'inStoreDirectIntegrationEnabled']),
    ...mapGetters('userProfile', ['selectedMerchant']),
    rules() {
      return {
        checkEmpty: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
        ],
        store: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
        ],
        phoneNumber: [
          {
            required: this.orderType === ORDER_TYPE_PAYMENT_LINK,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkPhone, trigger: 'blur' },
        ],
        email: [
          {
            required: false,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkEmail, trigger: 'blur' },
        ],
        expiryTime: [{ validator: this.checkExpiryTime, trigger: 'blur' }],
        amount: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkAmount, trigger: 'blur' },
        ],
        subventionType: [
          {
            required: this.shouldShowCampaignDropdown,
            message: this.$t('order_create_campaign_validation'),
            trigger: 'blur',
          },
        ],
      }
    },
    shouldShowCampaignDropdown() {
      return this.$abtProvider.isFeatureEnabled(
        'merchant_subvention_dropdown_enabled'
      )
    },
    isDisabledPaymentLinkSubmit() {
      return (
        this.loading ||
        this.isDisabledSubmitLink ||
        this?.$refs?.orderCreationForm?.fields[6]?.validateState === 'error'
      )
    },
    activeBranches() {
      if (!this.merchantBranches) {
        return []
      }
      return this.merchantBranches.filter(
        (branch) => branch.branchStatus === 'active'
      )
    },
    isDisabledSubmitQR() {
      return (
        !this.orderCreationForm.amount ||
        (this.merchantHasActiveBranches && !this.orderCreationForm.store)
      )
    },
    isDisabledSubmitLink() {
      return (
        !this.orderCreationForm.amount ||
        (this.merchantHasActiveBranches && !this.orderCreationForm.store) ||
        !this.orderCreationForm.phoneNumber
      )
    },

    order() {
      return order
    },
    isDialogShowing() {
      return Object.values(this.dialogs).some((value) => value === true)
    },
    timer() {
      const minutes = Math.floor(this.timerSeconds / 60)
      const seconds = this.timerSeconds % 60
      if (minutes === 0 && seconds === 0) {
        return this.$t('qr_code_expired_label')
      }
      return `${minutes}:${seconds}`
    },
    userCanCreateInStoreOrder() {
      return (
        this.profile &&
        this.profile.inStore &&
        userHasPermission(['order_create'])
      )
    },
    merchantHasActiveBranches() {
      return (
        this.activeBranches &&
        Array.isArray(this.activeBranches) &&
        this.activeBranches.length !== 0
      )
    },
    countryDecimal() {
      return this.merchantCountryData?.countryCode?.toLowerCase() === 'kw'
        ? '0.000'
        : '0.00'
    },
    orderDetail() {
      return this.getOrderDetail(this.createdOrderResponse.orderId)
    },
    merchantCountryData() {
      if (!this.profile || !this.profile.country) {
        return {}
      }
      return countryData.find(
        (item) =>
          item.countryCode.toLowerCase() === this.profile.country.toLowerCase()
      )
    },
    futureTime() {
      let futureTime = addMinutes(this.inStoreOrderCreationTime, 15)
      if (this.orderCreationForm.expiryTime) {
        futureTime = addMinutes(
          this.inStoreOrderCreationTime,
          this.getExpiryTimeInMinutes(this.orderCreationForm.expiryTime)
        )
      }
      return futureTime
    },
    linkExpireTimer() {
      if (!this.inStoreOrderCreationTime) {
        return
      }

      const dateFormat = 'dd/MM/yy, HH:mm a'
      return formatDate(this.futureTime, dateFormat)
    },
    barcodeValue() {
      if (
        !this.orderDetail ||
        Object.keys(this.orderDetail).length === 0 ||
        this.orderDetail.status !== 'fully_captured'
      ) {
        return
      }
      let barcodeValue = ''
      if (
        this.settings['barcode.approvalCode'] &&
        this.settings['barcode.amount']
      ) {
        barcodeValue = `${this.orderDetail.orderReferenceId};${this.orderDetail.totalAmount.amount} ${this.orderDetail.totalAmount.currency}`
      } else if (this.settings['barcode.approvalCode']) {
        barcodeValue = `${this.orderDetail.orderReferenceId}`
      } else if (this.settings['barcode.amount']) {
        barcodeValue = `${this.orderDetail.totalAmount.amount} ${this.orderDetail.totalAmount.currency}`
      }
      return barcodeValue
    },
    barcodeValueWillBeDisplayed() {
      return formatBarcodeValue(this.barcodeValue)
    },
    isPaymentPlanDisabled() {
      return (
        !(this.orderCreationForm.amount && this.orderCreationForm.amount > 0) ||
        this.paymentPlans.length === 0
      )
    },
    mapPaymentPlans() {
      if (this.paymentPlans && this.paymentPlans.length > 0) {
        const paymentPlans = this.paymentPlans.map((item) => {
          return {
            paymentType: item.name,
          }
        })
        return paymentPlans
      }
      return []
    },
    isArabic() {
      return this.currentLocale === 'ar_SA'
    },
    isOnlyPayNowAvailable() {
      return (
        this.paymentPlans.length === 1 && this.paymentPlans[0].name === PAY_NOW
      )
    },
    amountPlaceholder() {
      const formattedCurrency = formatCurrency(
        this.merchantCountryData.currency
      )

      return `${formattedCurrency} ${this.countryDecimal}`
    },
    merchantCountryCode() {
      return this.merchantCountryData?.countryCode?.toLowerCase()
    },
    isSelectedQR() {
      return this.orderType === ORDER_TYPE_QR_CODE
    },
    isSelectedLink() {
      return this.orderType === ORDER_TYPE_PAYMENT_LINK
    },
    isLoading() {
      return this.creatingOrder || this.creatingInStoreOrder
    },
    isDisabledSubmitButton() {
      if (this.isLoading) {
        return true
      }

      if (this.isSelectedQR) {
        return this.isDisabledSubmitQR
      }

      if (this.isSelectedLink) {
        return this.isDisabledSubmitLink
      }

      return false
    },
    isCashierBannerEnabled() {
      return (
        this.$abtProvider.isFeatureEnabled(
          'gate_temp_merchant_instore_cashier_view'
        ) && this.userCanCreateInStoreOrder
      )
    },
  },
  watch: {
    orderDetail: function(newValue, oldValue) {
      if (!newValue || Object.keys(newValue).length === 0) {
        return
      }

      if (newValue.status === 'declined' || newValue.status === 'expired') {
        clearInterval(this.intervalId)
        this.showDialog('failedDialog')
      }

      if (newValue.status === 'fully_captured') {
        clearInterval(this.intervalId)
        this.showDialog('successDialog')
      }
    },
    counter: function(newValue, oldValue) {
      if (newValue <= 0) {
        clearInterval(this.intervalId)
        this.linkExpired = true
      }
    },
    visible: function(newValue) {
      this.visibleDialog = newValue
      this.fetchBranches()
      this.getBarcodeSetting({ merchantId: this.profile.merchantId })
      this.setPreferredTypeSelection()
      window.addEventListener('beforeunload', this.reloadHandler)
      this.orderCreationForm.locale =
        this.merchantCountryData?.countryCode?.toLowerCase() === 'sa'
          ? 'ar_SA'
          : 'en_US'
    },
    activeBranches: function(newValue) {
      if (newValue.length && !this.orderCreationForm.store) {
        this.orderCreationForm.store = newValue[0]?.name
      }
      // Set default branch
    },
    orderType: function() {
      if (this.activeBranches.length && !this.orderCreationForm.store) {
        this.orderCreationForm.store = this.activeBranches[0]?.name
      }
      // Set default branch when order type change
    },
  },
  mounted() {
    if (this.paymentPlans.length === 1) {
      this.orderCreationForm.paymentType = this.paymentPlans[0].name
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId)
    window.removeEventListener('beforeunload', this.reloadHandler)
  },
  methods: {
    ...mapGetters('translations', ['currentDirection']),
    ...mapActions('order', [
      'createInStoreOrder',
      'createInStoreOrderQr',
      'cancelInStoreOrder',
      'fetchOrderDetail',
      'fetchPaymentPlans',
    ]),
    ...mapActions('barcodeSettings', ['getBarcodeSetting']),
    ...mapActions('order', ['fetchMerchantBranches', 'fetchPaymentPlans']),
    handleBackToEdit() {
      clearInterval(this.timerInterval)
      clearInterval(this.intervalId)

      this.hideAllDialogs()
    },
    toggleAdvance() {
      this.isAdvance = !this.isAdvance
    },
    handleClose() {
      // this.resetForm('orderCreationForm')
      // this.resetForm('orderCreationFormQR')
      this.visibleDialog = false
      this.isAdvance = false
      this.close()
      if (this.dialogs.successDialog || this.dialogs.failedDialog) {
        this.hideAllDialogs()
        this.counter = 0
        this.orderDetail.status = null
      }
    },
    customPaymentTypeFormatter(paymentType) {
      const key = this.$options.filters.paymentTypeFormatter(paymentType)

      // Split in 3 is different only in this component, it should show up as Pay By Installments
      if (key === 'payment_type_instalment') {
        return 'order_create_pay_by_installments'
      }
      return key
    },
    async fetchBranches() {
      const merchantIds = [this.selectedMerchant]
      await this.fetchMerchantBranches(merchantIds)
    },
    backToList() {
      navigateToOrderList(this.$router)
    },
    setPreferredTypeSelection() {
      // Check if both solutions are enabled
      if (
        this.profile.inStoreQrCodeSolutionEnabled &&
        this.profile.inStorePaymentLinkEnabled
      ) {
        const preferred = window.localStorage.getItem(ORDER_TYPE_STORAGE_KEY)

        if (preferred) {
          this.orderType = preferred
        }

        this.allowTypeSelection = true
      } else if (this.profile.inStoreQrCodeSolutionEnabled) {
        // Only QR code is enabled
        this.allowTypeSelection = false
        this.orderType = ORDER_TYPE_QR_CODE
      } else if (this.profile.inStorePaymentLinkEnabled) {
        // Only payment link is enabled
        this.allowTypeSelection = false
        this.orderType = ORDER_TYPE_PAYMENT_LINK
      }
    },
    updatePreferredType(orderType) {
      window.localStorage.setItem(ORDER_TYPE_STORAGE_KEY, orderType)

      this.orderType = orderType

      this.resetForm('orderCreationForm')
      this.resetForm('orderCreationFormQR')
    },
    // fetch order details to see if it has been maximum of 180 times (re-fetch for every 5 seconds)
    startExpireLinkTimer() {
      clearInterval(this.intervalId)

      this.startVisualTimer()
      const now = new Date()
      // Calculate the number of iterations based on the expiry time or set it to a maximum of 180
      this.counter = this.futureTime
        ? Math.round(differenceInSeconds(this.futureTime, now) / 5)
        : 180
      // Set an interval to decrement the counter and check conditions to stop the timer
      this.intervalId = setInterval(() => {
        // Decrement the counter
        this.counter--

        if (this.orderDetail.status === 'expired') {
          clearInterval(this.intervalId)
          this.linkExpired = true
          return
        }

        // Check if the order detail is fully captured
        if (
          this.orderDetail &&
          Object.keys(this.orderDetail).length !== 0 &&
          this.orderDetail.status === 'fully_captured'
        ) {
          clearInterval(this.intervalId)
          return
        }

        // UI timer exhausted before the backend confirmed expiry — unblock the button
        if (this.counter <= 0) {
          clearInterval(this.intervalId)
          this.$store.commit('order/SET_DRAWER_ACTIVE_ORDER_ID', null)
          return
        }

        // Call the getOrder() method after each iteration
        this.getOrder()
      }, 5000)
    },
    startVisualTimer() {
      clearInterval(this.timerInterval)
      // Setting 15 minutes timer;
      this.timerSeconds = 15 * 60
      // Set an interval to decrement the timer value
      this.timerInterval = setInterval(() => {
        // Decrement the timer value by 1 second
        this.timerSeconds--
        // If the timer reaches 0, clear the interval
        if (this.timerSeconds === 0) {
          clearInterval(this.timerInterval)
        }
      }, 1000)
    },
    async getOrder() {
      if (!this.fetchingDetail) {
        const isPingToFetchOrderDetail = 1

        await this.fetchOrderDetail({
          orderId: this.createdOrderResponse.orderId,
          merchantId: this.profile.merchantId,
          ping: isPingToFetchOrderDetail,
        })
      }
    },
    reloadHandler(event) {
      if (this.visibleDialog) {
        // Cancel the event as stated by the standard.
        event.preventDefault()
        // Chrome requires returnValue to be set.
        event.returnValue = ''
      }
    },
    resetForm(formName) {
      if (this.$refs[formName]) {
        this.$refs[formName].resetFields()
      }
      this.orderCreationForm = {
        phoneNumber: '',
        store: '',
        locale: 'ar_SA',
        email: '',
        orderReferenceId: '',
        orderReferenceIdQR: '',
        amount: '',
        paymentType: '',
        expiryTime: '',
      }
    },
    async showDialog(dialogName) {
      await this.hideAllDialogs()
      this.dialogs[dialogName] = true
    },
    hideAllDialogs() {
      Object.keys(this.dialogs).forEach((dialog, index) => {
        this.dialogs[dialog] = false
      })
    },
    checkPhone(rule, value, callback) {
      if (this.orderType !== ORDER_TYPE_PAYMENT_LINK) {
        callback()
        return
      }
      if (!value) {
        callback(new Error(this.$t('general_empty_field_message')))
      }
      const isNum = /^\d+$/.test(value)
      if (!isNum) {
        callback(new Error(this.$t('general_numbers_only_field_message')))
      }
      if (value.length < this.merchantCountryData.numberMinLength) {
        callback(
          new Error(
            `"${this.merchantCountryData.countryName}" ${this.$t(
              'general_phone_validation_text1'
            )} ${this.merchantCountryData.numberMinLength} ${this.$t(
              'general_phone_validation_text2'
            )}`
          )
        )
      }
      if (value.length > this.merchantCountryData.numberMaxLength) {
        callback(
          new Error(
            `"${this.merchantCountryData.countryName}" ${this.$t(
              'general_phone_validation_text3'
            )} ${this.merchantCountryData.numberMaxLength} ${this.$t(
              'general_phone_validation_text2'
            )}`
          )
        )
      }
      const firstNumber = Number(value.substring(0, 1))
      if (!this.merchantCountryData.numberStartsWith.includes(firstNumber)) {
        const message =
          this.currentLocale === 'ar_SA'
            ? `يرجي ادخال رقم خاتف خاص بـ "${this.merchantCountryData.countryName}" صحيح `
            : `Please enter a valid "${this.merchantCountryData.countryName}" phone number`
        callback(new Error(message))
      }

      callback()
    },
    checkEmail(rule, value, callback) {
      if (value === '') {
        callback()
        return
      }
      if (!validEmail(value)) {
        callback(new Error(this.$t('general_email_validation_text1')))
        return
      }
      callback()
    },
    checkExpiryTime(rule, value, callback) {
      if (!value) {
        callback()
        return
      }
      if (this.getExpiryTimeInMinutes(value) < 5) {
        callback(new Error(this.$t('expiry_time_error_minimum')))
      }
      callback()
    },
    checkAmount(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t('general_empty_field_message')))
      }
      if (decimalCount(value) > this.merchantCountryData.decimalDigits) {
        callback(
          new Error(
            `${this.$t('general_amount_validation_text1')} ${
              this.merchantCountryData.decimalDigits
            } ${this.$t('general_amount_validation_text2')}`
          )
        )
      }

      callback()
    },
    async submitQrForm(formName) {
      const that = this

      if (this.paymentPlans.length === 0) {
        await this.onChangeAmount(this.orderCreationForm.amount)
      }

      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const orderData = {
            totalAmount: {
              amount: this.orderCreationForm.amount,
              currency: this.merchantCountryData.currency,
            },
            orderReferenceId: this.orderCreationForm.orderReferenceIdQR,
            additionalData: { store_code: this.orderCreationForm.store },
            locale: this.orderCreationForm.locale,
            paymentType: this.orderCreationForm.paymentType,
            paymentPlans: this.paymentPlans,
          }

          if (
            this.shouldShowCampaignDropdown &&
            this.orderCreationForm.subventionType &&
            this.orderCreationForm.subventionType !== 'NOT_ELIGIBLE'
          ) {
            orderData.subventionType = this.orderCreationForm.subventionType
          }

          try {
            that.showDialog('loadingDialog')
            const response = await that.createInStoreOrderQr(orderData)

            this.inStoreOrderCreationTime = new Date()
            this.createdOrderResponse = response
            this.$store.commit(
              'order/SET_DRAWER_ACTIVE_ORDER_ID',
              response.orderId
            )

            this.qrCodeLink = this.createdOrderResponse.checkoutDeeplink
            // fetch order details till it is fully captured to show the success dialog
            this.linkExpired = false
            this.startExpireLinkTimer()
            this.showDialog('qrDialog')

            this.startExpireLinkTimer()

            return true
          } catch (err) {
            this.dialogs.loadingDialog = false
            this.showErrorMessage(err)
            return false
          }
        } else {
          return false
        }
      })
    },
    async submitPaymentLinkForm(formName, isResendLink) {
      const that = this

      if (this.paymentPlans.length === 0) {
        await this.onChangeAmount(this.orderCreationForm.amount)
      }

      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const expiryTimeInMinutes = this.getExpiryTimeInMinutes(
            this.orderCreationForm.expiryTime,
            isResendLink
          )

          const orderData = {
            totalAmount: {
              amount: this.orderCreationForm.amount,
              currency: this.merchantCountryData.currency,
            },
            phoneNumber:
              this.merchantCountryData.telephoneCode +
              this.orderCreationForm.phoneNumber,
            orderReferenceId: this.orderCreationForm.orderReferenceId,
            email: this.orderCreationForm.email,
            additionalData: { store_code: this.orderCreationForm.store },
            locale: this.orderCreationForm.locale,
            paymentType: this.orderCreationForm.paymentType,
            expiryTime: expiryTimeInMinutes,
            paymentPlans: this.paymentPlans,
          }

          if (
            this.shouldShowCampaignDropdown &&
            this.orderCreationForm.subventionType &&
            this.orderCreationForm.subventionType !== 'NOT_ELIGIBLE'
          ) {
            orderData.subventionType = this.orderCreationForm.subventionType
          }

          try {
            that.showDialog('loadingDialog')
            const response = await that.createInStoreOrder(orderData)
            this.inStoreOrderCreationTime = new Date()
            this.createdOrderResponse = response
            this.$store.commit(
              'order/SET_DRAWER_ACTIVE_ORDER_ID',
              response.orderId
            )
            this.linkExpired = false
            this.startExpireLinkTimer()

            that.showDialog('smsSentDialog')
            return true
          } catch (err) {
            that.hideAllDialogs()
            this.showErrorMessage(err)
          }
        } else {
          return false
        }
      })
    },
    getExpiryTimeInMinutes(datetime, isResendLink) {
      if (!datetime) {
        return ''
      }

      if (!isResendLink) {
        const calculatedExpiryTime =
          moment(datetime).hours() * 60 + moment(datetime).minutes()
        this.cacheExpiryTimeInMinutes = calculatedExpiryTime
        return calculatedExpiryTime
      } else {
        this.orderCreationForm.expiryTime = moment()
          .add(this.cacheExpiryTimeInMinutes, 'minutes')
          .toDate()
        return this.cacheExpiryTimeInMinutes
      }
    },
    showErrorMessage(err) {
      if (typeof err === 'string' && (err === 'cancel' || err === 'close')) {
        return
      }

      const PRE_CHECKOUT_NOT_ELIGIBLE = 'pre_checkout_eligibility.not_eligible'
      const response = err.response || {}
      const { data } = response
      const errorCode =
        data && data.errors && data.errors.length > 0
          ? data.errors[data.errors.length - 1]?.error_code
          : ''
      const messageCode = data?.message

      let translationKey
      if (errorCode === 'payment_type_unsupported') {
        translationKey = 'error_order_creation_payment_type_unsupported'
      } else if (messageCode === PRE_CHECKOUT_NOT_ELIGIBLE) {
        translationKey = 'error_order_creation_pre_checkout_not_eligible'
      } else {
        translationKey = errorCode
      }

      const title = this.$te(`${translationKey}_title`)
        ? this.$t(`${translationKey}_title`)
        : this.$t('general_error_title')

      const message = this.$te(`${translationKey}_subtitle`)
        ? this.$t(`${translationKey}_subtitle`)
        : (data && data.message) || this.$t('general_error_message')
      const messageUnderline = this.$te(`${translationKey}_subtitle_underline`)
        ? this.$t(`${translationKey}_subtitle_underline`)
        : ''
      this.error = {
        title,
        message,
        messageUnderline,
      }
      this.showDialog('errorDialog')
    },
    filterMerchantById(id) {
      return this.subMerchants.filter((obj) => {
        return obj.merchantId === id
      })
    },
    calcMerchantName(merchant) {
      if (this.currentLocale === 'ar_SA') {
        return merchant.arMerchantName || merchant.merchantName
      }
      return merchant.merchantName || merchant.arMerchantName
    },
    cancelOrderClicked() {
      this.showDialog('cancelConfirmationDialog')
    },
    async cancelOrderProcess() {
      this.showDialog('loadingDialog')
      this.resetForm('orderCreationForm')
      this.resetForm('orderCreationFormQR')

      const data = {
        checkoutId: this.createdOrderResponse.checkoutId,
        orderId: this.createdOrderResponse.orderId,
      }
      await this.cancelInStoreOrder(data)
      this.$store.commit('order/SET_DRAWER_ACTIVE_ORDER_ID', null)
      this.hideAllDialogs()
      this.counter = 0
    },
    goToOrderDetails() {
      this.closeLastStepDialog()
      setTimeout(() => {
        this.$router.push({
          path: `/order/detail/${this.createdOrderResponse.orderId}?merchantId=${this.profile.merchantId}`,
        })
      }, 500)
    },
    stayOnPage() {
      this.orderType === ORDER_TYPE_QR_CODE
        ? this.showDialog('qrDialog')
        : this.showDialog('smsSentDialog')
    },
    closeLastStepDialog() {
      this.handleClose()
      this.hideAllDialogs()
      this.counter = 0
    },
    async onChangeAmount(value) {
      if (value > 0) {
        await this.fetchPaymentPlans({
          countryCode: this.merchantCountryData.countryCode,
          amount: value,
          currency: this.merchantCountryData.currency,
        })

        this.orderCreationForm.paymentType = ''
        if (this.paymentPlans.length === 1) {
          this.orderCreationForm.paymentType = this.paymentPlans[0].name
        }
      }
    },
    handleSubmitCreateOrder() {
      if (this.orderType === ORDER_TYPE_QR_CODE) {
        this.submitQrForm('orderCreationFormQR')

        return
      }

      this.submitPaymentLinkForm('orderCreationForm', false)
    },
  },
}
</script>

<template>
  <div
    :class="
      (isAdvance || inStoreDirectIntegrationEnabled) &&
      !isDialogShowing &&
      orderType === ORDER_TYPE_PAYMENT_LINK
        ? 'create-order create-order-advance'
        : 'create-order'
    "
  >
    <el-drawer
      :direction="currentLocale === 'ar_SA' ? 'ltr' : 'rtl'"
      :with-header="false"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="create-order-drawer"
      :before-close="handleClose"
      :visible="
        visibleDialog &&
          userCanCreateInStoreOrder &&
          (profile.inStoreQrCodeSolutionEnabled ||
            profile.inStorePaymentLinkEnabled)
      "
    >
      <div class="create-order__header">
        {{ $t('create_order_drawer_title') }}
        <span class="create-order__header__close-button" @click="handleClose">
          <svg-icon icon-class="close" size="24px" />
        </span>
      </div>
      <div class="create-order__body">
        <div style="margin-block: 24px;">
          <CashierNewOrderDrawerBanner v-if="isCashierBannerEnabled" />
        </div>
        <div
          v-if="!isDialogShowing && allowTypeSelection"
          class="create-order__payment-type-button__group"
        >
          <BaseButtonV2
            size="large"
            :type="orderType === ORDER_TYPE_QR_CODE ? 'accent' : 'tertiary'"
            class="create-order__payment-type-button"
            track-id="create_order_type_qr_code"
            @click="() => updatePreferredType(ORDER_TYPE_QR_CODE)"
          >
            <svg-icon
              :icon-class="
                orderType === ORDER_TYPE_QR_CODE
                  ? 'qr-order-active'
                  : 'qr-order'
              "
            />

            {{ $t('create_order_type_qr_code') }}
          </BaseButtonV2>
          <BaseButtonV2
            size="large"
            :type="
              orderType === ORDER_TYPE_PAYMENT_LINK ? 'accent' : 'tertiary'
            "
            class="create-order__payment-type-button"
            track-id="create_order_type_payment_link"
            @click="() => updatePreferredType(ORDER_TYPE_PAYMENT_LINK)"
          >
            <svg-icon
              :icon-class="
                orderType === ORDER_TYPE_PAYMENT_LINK
                  ? 'payment-link-active'
                  : 'payment-link'
              "
            />

            {{ $t('create_order_type_payment_link') }}
          </BaseButtonV2>
        </div>
        <div
          v-if="!isDialogShowing"
          class="create-order__advance tamara-english-regular-2"
        >
          {{ $t('create_order_drawer_switch') }}
          <BaseSwitch
            v-model="isAdvance"
            type="secondary"
            class="create-order__switch"
            @click="toggleAdvance"
          />
        </div>

        <el-form
          v-show="orderType === ORDER_TYPE_QR_CODE && !isDialogShowing"
          ref="orderCreationFormQR"
          :model="orderCreationForm"
          :disabled="isLoading"
        >
          <el-form-item
            :label="$t('order_create_order_amount')"
            prop="amount"
            :rules="rules.amount"
          >
            <el-input
              v-model="orderCreationForm.amount"
              class="value-field global__font-currency-primary"
              :placeholder="amountPlaceholder"
              @change="onChangeAmount"
            />
          </el-form-item>
          <el-form-item
            :label="$t('order_create_receipt_language')"
            prop="locale"
            :rules="rules.checkEmpty"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.locale"
              placeholder="Select"
              popper-class="global__select global__select--new-ui is-lavender-design"
              class="w-full"
            >
              <el-option
                v-for="item in locales"
                :key="item.value"
                :label="item.name"
                :value="item.value"
              >
                <span class="flex items-center justify-between align-middle">
                  <span>
                    {{ item.name }}
                  </span>
                  <svg-icon
                    v-if="item.value === orderCreationForm.locale"
                    icon-class="check"
                  />
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="merchantHasActiveBranches"
            :label="$t('order_create__store')"
            prop="store"
            :rules="rules.checkEmpty"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.store"
              style="width: 100%"
              size="large"
              popper-class="global__select global__select--new-ui global__select--new-ui-multiple is-lavender-design is-lavender-design"
              :placeholder="$t('order_list_select_store')"
              filterable
              clearable
            >
              <el-option
                v-for="(item, index) in activeBranches"
                :key="index"
                :label="ellipsisEnd(item.name, 40)"
                :value="item.name"
              >
                <span>
                  <span>
                    <el-tooltip
                      placement="right"
                      :open-delay="200"
                      :content="item.name"
                    >
                      <div>
                        <div>
                          <div>
                            {{ ellipsisEnd(item.name, 40) }}
                          </div>
                        </div>
                        <div class="global__select-check-icon">
                          <svg-icon
                            v-if="item.name === storeSelected"
                            icon-class="check"
                          />
                        </div>
                      </div>
                    </el-tooltip>
                  </span>
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="shouldShowCampaignDropdown"
            :label="$t('order_create_campaign_type')"
            prop="subventionType"
            :rules="rules.subventionType"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.subventionType"
              :placeholder="$t('order_create_campaign_type_placeholder')"
              popper-class="global__select global__select--new-ui is-lavender-design"
              class="w-full"
            >
              <el-option
                v-for="item in campaignOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
                <span class="flex items-center justify-between align-middle">
                  <span>
                    {{ item.label }}
                  </span>
                  <svg-icon
                    v-if="item.value === orderCreationForm.subventionType"
                    icon-class="check"
                  />
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="isAdvance"
            prop="orderReferenceIdQR"
            class="value-field"
          >
            <span slot="label">
              {{ $t('order_create_order_reference') }}
              <span>{{ $t('order_create_optional') }}</span>
            </span>
            <el-input
              v-model="orderCreationForm.orderReferenceIdQR"
              :placeholder="$t('order_create_order_reference_placeholder')"
              class="value-field"
            />
          </el-form-item>
        </el-form>

        <el-form
          v-show="orderType === ORDER_TYPE_PAYMENT_LINK && !isDialogShowing"
          ref="orderCreationForm"
          :model="orderCreationForm"
          :disabled="creatingOrder"
          :class="isAdvance ? 'create-order-advance' : ''"
        >
          <el-form-item
            :label="$t('order_create_order_amount')"
            prop="amount"
            :rules="rules.amount"
          >
            <el-input
              v-model="orderCreationForm.amount"
              :placeholder="amountPlaceholder"
              class="value-field global__font-currency-primary"
              @change="onChangeAmount"
            />
          </el-form-item>
          <el-form-item
            :label="$t('order_create_customer_phone_number')"
            prop="phoneNumber"
            :rules="rules.phoneNumber"
          >
            <el-input
              v-model="orderCreationForm.phoneNumber"
              :placeholder="$t('order_create_phone_number')"
              type="tel"
              pattern="[0-9]*"
              inputmode="numeric"
              class="value-field create-order__phone-field"
              @keypress.native.stop="isNumber"
            >
              <template :slot="isArabic ? 'append' : 'prepend'">
                <span class="create-order__phone-prefix">
                  <div>
                    <svg-icon
                      class="create-order__phone-prefix-flag"
                      :icon-class="merchantCountryCode"
                      size="24px"
                    />
                  </div>
                  <div class="global__always-ltr">
                    +{{ merchantCountryData.telephoneCode }}
                  </div>
                </span>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item
            v-if="merchantHasActiveBranches"
            :label="$t('order_create__store')"
            prop="store"
            :rules="rules.checkEmpty"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.store"
              style="width: 100%"
              size="large"
              popper-class="global__select global__select--new-ui global__select--new-ui-multiple is-lavender-design"
              :placeholder="$t('order_list_select_store')"
              filterable
              clearable
            >
              <el-option
                v-for="(item, index) in activeBranches"
                :key="index"
                :label="ellipsisEnd(item.name, 40)"
                :value="item.name"
              >
                <span>
                  <span>
                    <el-tooltip
                      placement="right"
                      :open-delay="200"
                      :content="item.name"
                    >
                      <div>
                        <div>
                          <div>
                            {{ ellipsisEnd(item.name, 40) }}
                          </div>
                        </div>
                        <div class="global__select-check-icon">
                          <svg-icon
                            v-if="item.name === storeSelected"
                            icon-class="check"
                          />
                        </div>
                      </div>
                    </el-tooltip>
                  </span>
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('order_create_receipt_language')"
            prop="locale"
            :rules="rules.checkEmpty"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.locale"
              placeholder="Select"
              popper-class="global__select global__select--new-ui is-lavender-design"
              class="w-full"
            >
              <el-option
                v-for="item in locales"
                :key="item.value"
                :label="item.name"
                :value="item.value"
              >
                <span class="flex items-center justify-between align-middle">
                  <span>
                    {{ item.name }}
                  </span>
                  <svg-icon
                    v-if="item.value === orderCreationForm.locale"
                    icon-class="check"
                  />
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="shouldShowCampaignDropdown"
            :label="$t('order_create_campaign_type')"
            prop="subventionType"
            :rules="rules.subventionType"
            class="value-field"
          >
            <el-select
              v-model="orderCreationForm.subventionType"
              :placeholder="$t('order_create_campaign_type_placeholder')"
              popper-class="global__select global__select--new-ui is-lavender-design"
              class="w-full"
            >
              <el-option
                v-for="item in campaignOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
                <span class="flex items-center justify-between align-middle">
                  <span>
                    {{ item.label }}
                  </span>
                  <svg-icon
                    v-if="item.value === orderCreationForm.subventionType"
                    icon-class="check"
                  />
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="inStoreDirectIntegrationEnabled || isAdvance"
            prop="orderReferenceId"
            :rules="inStoreDirectIntegrationEnabled ? rules.checkEmpty : {}"
          >
            <span slot="label">
              {{ $t('order_create_order_reference') }}
              <span v-if="!inStoreDirectIntegrationEnabled">
                {{ $t('order_create_optional') }}
              </span>
            </span>
            <el-input
              v-model="orderCreationForm.orderReferenceId"
              :placeholder="$t('order_create_order_reference_placeholder')"
              class="value-field"
            />
          </el-form-item>

          <el-form-item v-if="isAdvance" prop="email" :rules="rules.email">
            <span slot="label">
              {{ $t('order_create_email') }}
              <span>{{ $t('order_create_optional') }}</span>
            </span>
            <el-input
              v-model="orderCreationForm.email"
              :placeholder="'name@example.com'"
              class="value-field"
            />
          </el-form-item>

          <el-form-item
            v-if="isAdvance"
            prop="expiryTime"
            class="value-field"
            :rules="rules.expiryTime"
          >
            <span slot="label">
              {{ $t('order_create_expiry_time') }}
              <span>{{ $t('order_create_optional') }}</span>
            </span>
            <el-time-picker
              v-model="orderCreationForm.expiryTime"
              style="width: 100%"
              size="large"
              :format="'HH : mm'"
              :disabled-hours="[0]"
              :disabled-minutes="[0, 1, 2, 3, 4, 5]"
              :default-value="defaultTime"
              :teleported="false"
              popper-class="create-order-advance__time-picker-popper"
              placeholder="hh:mm"
            />
          </el-form-item>

          <el-form-item
            v-if="isOnlyPayNowAvailable"
            :label="$t('order_create_payment_plan')"
            prop="paymentType"
            :rules="rules.checkEmpty"
          >
            <el-select
              v-model="orderCreationForm.paymentType"
              :placeholder="$t('order_create_payment_plan_placeholder')"
              popper-class="global__select global__select--new-ui is-lavender-design"
              class="w-full"
              :disabled="isPaymentPlanDisabled"
            >
              <el-option
                v-for="item in mapPaymentPlans"
                :key="item.paymentType"
                :label="
                  customPaymentTypeFormatter(item.paymentType || 'N/A')
                    | translatePaymentType(3)
                "
                :value="item.paymentType"
              >
                <span class="flex items-center justify-between align-middle">
                  <span>
                    {{
                      customPaymentTypeFormatter(item.paymentType || 'N/A')
                        | translatePaymentType(3)
                    }}
                  </span>
                </span>
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>

        <!-- <div> -->
        <div v-if="dialogs['qrDialog']">
          <h2 class="create-order__dialog__heading--qr">
            <!-- {{ $t('qr_dialog_heading_normal') }}
          {{ $t('qr_dialog_heading_bold') }} -->
            Scan using Tamara app
          </h2>
          <div class="qr-wrapper">
            <QrcodeVue
              :value="qrCodeLink"
              class-name="qr-code__wrap"
              :size="200"
            />
          </div>

          <div class="create-order__dialog__timer">
            <span v-if="!linkExpired">
              {{ $t('qr_code_expiry_label') }}
            </span>
            {{ timer }}
          </div>

          <el-divider />

          <div class="create-order__dialog__amount">
            <div class="create-order__dialog__amount__label">
              {{ $t('order_create_wait_total') }}
            </div>
            <div class="create-order__dialog__amount__total">
              <Amount
                align="center"
                :amount="orderCreationForm.amount"
                :currency="merchantCountryData.currency"
              />
            </div>
          </div>

          <span slot="footer">
            <div class="create-order__dialog__footer-buttons">
              <BaseButtonV2
                v-if="linkExpired"
                type="primary"
                track-id="create_order_link_generate"
                @click="submitQrForm('orderCreationForm')"
              >
                {{ $t('order_create_link_generate') }}
              </BaseButtonV2>
              <BaseButtonV2
                type="secondary"
                :is-wide="true"
                track-id="create_order_back_to_edit"
                @click="handleBackToEdit"
              >
                {{ $t('create_order_drawer_back_edit') }}
              </BaseButtonV2>
              <BaseButtonV2
                type="text"
                :is-wide="true"
                track-id="create_order_cancel"
                @click="cancelOrderClicked"
              >
                {{ $t('order_create_wait_cancel') }}
              </BaseButtonV2>
            </div>
          </span>
        </div>

        <div v-if="dialogs['smsSentDialog']" class="create-order__dialog">
          <a
            v-if="!linkExpired"
            class="el-dialog__headerbtn"
            type="text"
            target="_blank"
            :href="$t('zendesk_helper_url')"
          >
            <div class="create-order__question-icon-container">
              <svg-icon icon-class="circle-question-mark" />
            </div>
          </a>
          <span slot="title">
            <div class="create-order__dialog-icon">
              <i
                v-if="!linkExpired"
                class="el-icon-loading"
                style="font-size: 32px;"
              />
              <svg-icon v-if="linkExpired" icon-class="error" size="72px" />
            </div>
            <div v-if="!linkExpired" class="create-order__dialog-title">
              {{ $t('order_create_wait_title') }}
            </div>
            <div v-if="linkExpired" class="create-order__dialog-title">
              {{ $t('order_create_link_expired_title') }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div v-if="!linkExpired" class="create-order__dialog-content-text">
              {{ $t('order_create_wait_description') }}
            </div>
            <div v-if="linkExpired" class="create-order__dialog-content-text">
              {{ $t('order_create_link_expired_description') }}
            </div>
            <div class="create-order__dialog-content-box">
              <div>
                <div>{{ $t('order_create_wait_phone') }}</div>
                <div dir="ltr">
                  +{{
                    `${merchantCountryData.telephoneCode} ${orderCreationForm.phoneNumber}`
                  }}
                </div>
              </div>
              <div>
                <div>{{ $t('order_create_wait_total') }}</div>
                <Amount
                  :amount="orderCreationForm.amount"
                  :currency="merchantCountryData.currency"
                />
              </div>
              <div>
                <div>{{ $t('order_create_wait_link_expire') }}</div>
                <div
                  :class="linkExpired ? 'create-order__dialog-text-red' : ''"
                >
                  {{ linkExpireTimer }}
                </div>
              </div>
            </div>
          </div>
          <span slot="footer">
            <BaseButtonV2
              v-if="linkExpired"
              type="primary"
              :is-wide="true"
              track-id="create_order_payment_link_generate"
              @click="submitPaymentLinkForm('orderCreationForm', true)"
            >
              {{ $t('order_create_link_generate') }}
            </BaseButtonV2>
            <BaseButtonV2
              type="text"
              :is-wide="true"
              track-id="create_order_payment_cancel"
              @click="cancelOrderClicked"
            >
              {{ $t('order_create_wait_cancel') }}
            </BaseButtonV2>
          </span>
        </div>

        <div v-if="dialogs['loadingDialog']" class="create-order__dialog">
          <span slot="title">
            <div class="create-order__dialog-icon">
              <i class="el-icon-loading" />
            </div>
            <div class="create-order__dialog-title">
              {{ $t('order_create_loading_title') }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div class="create-order__dialog-content-text">
              {{ $t('order_create_loading_description') }}
            </div>
          </div>
        </div>
        <div
          v-if="dialogs['cancelConfirmationDialog']"
          class="create-order__dialog"
        >
          <span slot="title">
            <div
              class="create-order__dialog-title create-order__dialog-title-confirm"
            >
              {{ $t('order_create_cancel_title') }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div class="create-order__dialog-content-text">
              {{ $t('order_create_cancel_description') }}
            </div>
          </div>
          <span slot="footer">
            <div class="create-order__dialog__footer-buttons">
              <BaseButtonV2
                type="secondary"
                :is-wide="true"
                @click="stayOnPage"
              >
                {{ $t('order_create_cancel_button_stay') }}
              </BaseButtonV2>
              <BaseButtonV2
                type="text"
                :is-wide="true"
                @click="cancelOrderProcess"
              >
                {{ $t('order_create_cancel_button_cancel') }}
              </BaseButtonV2>
            </div>
          </span>
        </div>
        <div
          v-if="dialogs['successDialog']"
          :custom-class="
            'create-order__dialog create-order__dialog--flexible-width'
          "
        >
          <span slot="title">
            <div
              class="create-order__dialog-icon create-order__dialog-icon-success"
            >
              <svg-icon icon-class="success" size="72px" />
            </div>
            <div
              class="create-order__dialog-title create-order__dialog-title--success"
            >
              {{ $t('create_order_dialog_title_successful') }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div
              class="create-order__dialog-content-text create-order__dialog-content-code"
            >
              {{ $t('order_create_approval_code') }} #{{
                orderDetail.orderReferenceId
              }}
            </div>
            <div class="create-order__barcode-container">
              <VueBarcode
                v-if="barcodeValue"
                :value="barcodeValue"
                :display-value="false"
              />
            </div>
            <div class="create-order__barcode-value">
              #{{ barcodeValueWillBeDisplayed }}
            </div>
            <div class="create-order__divider create-order__divider-success" />

            <div
              class="create-order__dialog-content-box create-order__dialog-content-box-success"
            >
              <div>
                <div class="create-order__table-title">
                  {{ $t('order_create_complete_date') }}
                </div>
                <div class="create-order__table-value">
                  {{ orderDetail.createdAt | dateFormatter }},
                  {{ orderDetail.createdAt | timeFormatter }}
                </div>
              </div>
              <div>
                <div class="create-order__table-title">
                  {{ $t('order_create_wait_total') }}
                </div>
                <!--              <div> {{ `${merchantCountryData.currency} ${this.orderCreationForm.amount}` }}</div>-->
                <div class="create-order__table-value">
                  <Amount
                    :amount="orderDetail.totalAmount.amount"
                    :currency="orderDetail.totalAmount.currency"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            slot="footer"
            class="create-order__dialog__footer-buttons flex justify-between gap-4 mt-8"
          >
            <BaseButtonV2
              type="primary"
              :is-wide="true"
              track-id="create_order_see_details"
              @click="goToOrderDetails"
            >
              {{ $t('order_create_see_details') }}
            </BaseButtonV2>
            <BaseButtonV2
              type="secondary"
              :is-wide="true"
              track-id="create_order_success_close"
              @click="closeLastStepDialog"
            >
              {{ $t('order_create_close') }}
            </BaseButtonV2>
          </div>
        </div>
        <div
          v-if="orderDetail && dialogs['failedDialog']"
          :class="'create-order__dialog create-order__dialog--flexible-width'"
        >
          <span slot="title">
            <div
              class="create-order__dialog-icon create-order__dialog-icon-error"
            >
              <svg-icon icon-class="error" size="72px" />
            </div>
            <div class="create-order__dialog-title">
              {{ $t('order_create_failed_title') }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div class="create-order__dialog-content-text">
              {{
                orderDetail.status === 'expired'
                  ? $t('order_create_expired_description')
                  : $t('order_create_failed_description')
              }}
            </div>
          </div>
          <span slot="footer">
            <BaseButtonV2
              type="secondary"
              :is-wide="true"
              class="mt-8"
              track-id="create_order_failed_close"
              @click="closeLastStepDialog"
            >
              {{ $t('order_create_close') }}
            </BaseButtonV2>
          </span>
        </div>
        <div
          v-if="dialogs['errorDialog']"
          :class="'create-order__dialog create-order__dialog--flexible-width'"
        >
          <span slot="title">
            <div
              class="create-order__dialog-icon create-order__dialog-icon-error"
            >
              <svg-icon icon-class="error" size="72px" />
            </div>
            <div class="create-order__dialog-title">
              {{ error.title }}
            </div>
          </span>
          <div class="create-order__dialog-content">
            <div class="create-order__dialog-content-text">
              <template v-if="error.messageUnderline">
                {{ error.message }}
                <span class="create-order__dialog-content-text--underline">
                  {{ error.messageUnderline }}
                </span>
              </template>
              <template v-else>
                {{ $te(error.message) ? $t(error.message) : error.message }}
              </template>
            </div>
          </div>
          <span slot="footer">
            <BaseButtonV2
              type="secondary"
              :is-wide="true"
              class="mt-8"
              track-id="create_order_error_close"
              @click="handleBackToEdit"
            >
              {{ $t('order_create_close') }}
            </BaseButtonV2>
          </span>
        </div>
      </div>
      <div v-if="!isDialogShowing" class="create-order__footer">
        <BaseButtonV2
          type="tertiary"
          class="button-cancel"
          :loading="isLoading"
          :disabled="isLoading"
          track-id="create_order_cancel"
          @click="handleClose"
        >
          {{ $t('text_cancel') }}
        </BaseButtonV2>
        <BaseButtonV2
          type="primary"
          class="button-submit"
          :loading="isLoading"
          :disabled="isDisabledSubmitButton"
          track-id="create_order_submit"
          @click="handleSubmitCreateOrder"
        >
          {{ $t('order_create_place_an_order') }}
        </BaseButtonV2>
      </div>
    </el-drawer>
  </div>
</template>

<style lang="scss">
@import '@/styles/lavender/design.scss';

.create-order-drawer {
  min-width: 400px;
  max-width: 420px;

  .el-drawer__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 100vh;
    padding: 40px 24px;
  }

  .create-order__header {
    @extend .tamara-english-heading-6;

    display: flex;
    flex: 0 0 auto;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 24px;
    color: var(--tamara-color-content-high);
    border-bottom: 1px solid var(--tamara-color-border-mid);

    &__close-button {
      cursor: pointer;
      transition: color 0.2s ease-in-out;

      &:hover {
        color: var(--tamara-color-content-low);
        transition: color 0.2s ease-in-out;
      }
    }
  }

  .create-order__body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
  }

  .create-order__payment-type-button__group {
    display: flex;
    gap: 16px;
    width: 100%;

    .create-order__payment-type-button {
      flex: 1 1 auto;
      border-width: 1px;
      border-radius: 12px;
    }
  }

  // Toggle advance button
  .create-order__advance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    color: var(--tamara-color-content-mid);
  }

  // Form
  .el-form {
    padding-bottom: 12px;
    .el-form-item {
      margin-bottom: 20px;

      .el-form-item__label {
        @extend .tamara-english-regular-2;

        margin-bottom: 12px;
        color: var(--tamara-color-content-mid);
        text-align: start;

        [dir='rtl'] & {
          float: right;
          padding-right: 6px;
        }
      }

      .create-order__phone-field.el-input {
        display: flex;

        [dir='rtl'] & {
          flex-direction: row-reverse;
        }

        .el-input-group__append,
        .el-input-group__prepend {
          min-width: 100px;
          height: 100%;
          height: 48px;
          padding-inline-start: 12px;
          padding-inline-end: 8px;
          background: var(--tamara-color-background-bright);
          border: 1.5px solid var(--tamara-color-border-low) !important;
          border-top-left-radius: 12px;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 12px;

          [dir='rtl'] & {
            border-top-left-radius: 0;
            border-top-right-radius: 12px;
            border-bottom-right-radius: 12px;
            border-bottom-left-radius: 0;
          }

          .create-order__phone-prefix {
            display: flex;
            gap: 12px;
            align-items: center;
            justify-content: center;
            min-width: 72px;
            height: 48px;
            color: var(--tamara-color-content-high);

            @extend .tamara-english-regular-1;

            .create-order__phone-prefix-flag {
              width: 24px;
              height: 24px;
            }
          }
        }

        .el-input__inner {
          text-align: left;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;

          [dir='ltr'] & {
            border-left: none !important;
          }

          [dir='rtl'] & {
            text-align: right;
            border-right: none !important;
            border-top-left-radius: 12px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 12px;
          }
        }
      }

      .el-input__inner {
        @extend .tamara-english-regular-1;

        height: 48px;
        padding: 10px 12px;
        color: var(--tamara-color-content-high);
        background: var(--tamara-color-background-bright);
        border: 1.5px solid var(--tamara-color-border-low) !important;
        border-radius: 12px;

        &::placeholder {
          color: var(--tamara-color-content-low);
        }

        &:hover {
          border-color: var(--tamara-color-border-mid);
        }
      }

      .el-date-editor {
        .el-input__inner {
          padding-inline: 32px;
        }

        .el-input__prefix {
          padding-top: 4px;
        }
      }
    }
  }

  .create-order__footer {
    display: flex;
    flex: 0 0 auto;
    gap: 16px;
    justify-content: space-between;
    width: 100%;
    padding-top: 24px;
    border-top: 1px solid var(--tamara-color-border-mid);

    .button-cancel {
      flex: 2 2 auto;
    }

    .button-submit {
      flex: 3 3 auto;
    }
  }

  // QR Code
  .create-order__dialog__heading--qr {
    @extend .tamara-english-heading-6;

    margin-top: 32px;
    text-align: center;
  }

  .qr-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .create-order__dialog__timer {
    @extend .tamara-english-regular-2;

    margin-bottom: 12px;
    color: var(--tamara-color-content-mid);
    text-align: center;
  }

  .create-order__dialog__amount__label {
    @extend .tamara-english-regular-2;

    margin-bottom: 12px;
    color: var(--tamara-color-content-high);
    text-align: center;
  }

  .create-order__dialog__amount__total {
    @extend .tamara-english-heading-6;

    margin-bottom: 12px;
    text-align: center;
  }

  .create-order__dialog__footer-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 40px;
  }

  // SMS Sent Dialog
  .create-order__dialog-title {
    @extend .tamara-english-heading-6;

    margin-top: 32px;
    color: var(--tamara-color-content-high);
    text-align: center;
  }

  .create-order__dialog-content {
    @extend .tamara-english-regular-2;

    margin-top: 12px;
    color: var(--tamara-color-content-mid);
    text-align: start;
  }

  .create-order__dialog-content-box {
    margin-top: 24px;
    margin-bottom: 40px;
    color: var(--tamara-color-content-mid);
    border: 1.5px solid var(--tamara-color-border-low);
    border-radius: 12px;

    > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
      margin-right: 16px;
      margin-left: 16px;
      border-bottom: 1.5px solid var(--tamara-color-border-low);

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .create-order__dialog-content-text {
    @extend .tamara-english-regular-2;

    margin-top: 24px;
    color: var(--tamara-color-content-mid);
    text-align: center;
  }

  .create-order__dialog-content-text--underline {
    text-decoration: underline;
  }

  .create-order__dialog-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: 32px;
    padding-bottom: 24px;
    text-align: center;
  }

  // Success Dialog
  .create-order__barcode-container {
    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
  }

  .create-order__barcode-value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
}

.create-order-advance__time-picker-popper {
  &.el-time-panel {
    right: 53px !important;
    left: auto !important;
    width: 323px !important;

    [dir='rtl'] & {
      right: auto !important;
      left: 53px !important;
      direction: ltr;
    }
  }
}
</style>
